import { SortableContext } from "@dnd-kit/sortable";
import { plural } from "pluralize";
import { type FC, type ReactElement, useState } from "react";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";

import { ActionButton, ActionButtonIntent } from "widgets";
import { moveItemsInArray } from "widgets/utils";

import style from "../forms/styles/Sortable.module.scss";

import { SortableItem, SortableList } from "../forms/Sortable.js";
import { fieldIsInvalid } from "@/app-utils/form.ts";
import { capitalizeFirstLetter, pluralizeWord } from "@/app-utils/string.ts";
import { type ProjectFile } from "@/graphql.ts";
import {
  FilePickerControlledDialog,
  type FilePickerProps,
} from "@/modules/files/components/FilePickerControlledDialog.js";
import { getParentPath } from "@/modules/files/utils/file.js";
import { FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

export interface IFilePickerFieldProps extends Partial<FilePickerProps> {
  isDisabled?: boolean;
  onChange?: (value) => void;
  value?: unknown;
  noSelectionText?: string;
}

export const FilePickerField: FC<IFilePickerFieldProps> = (props) => {
  const [filePickerOpen, openFilePicker] = useState<boolean>(false);

  const selectFile =
    props.selectableFileTypes?.length === 1 && props.selectableFileTypes?.[0] == FILE_BROWSE_TYPES.REGULAR_FILE;
  const selectFolder =
    props.selectableFileTypes?.length === 1 && props.selectableFileTypes?.[0] == FILE_BROWSE_TYPES.DIRECTORY;

  const files = Array.isArray(props.value) ? props.value : props.value?.length ? [props.value?.length] : [];

  const removeFile = (index) => {
    props.onChange(files.filter((_file, filteringIndex) => index !== filteringIndex));
  };

  const resourceLabel = selectFile ? "file" : selectFolder ? "folder" : "resource";

  return (
    <div>
      <ActionButton
        intent={ActionButtonIntent.Primary}
        onPress={() => openFilePicker(true)}
        icon={"folder_open"}
        label={`Select ${capitalizeFirstLetter(!props.singleSelection ? plural(resourceLabel) : resourceLabel)}`}
      />
      &nbsp;&nbsp;
      {props.noSelectionText && !files.length ? (
        <div style={{ verticalAlign: "middle", display: "inline-block", width: "350px" }}>{props.noSelectionText}</div>
      ) : (
        <>
          {" "}
          {files.length ? files.length : "No"}&nbsp;{pluralizeWord(resourceLabel, files.length)}
          &nbsp;selected
        </>
      )}
      <SortableList
        id="files-pick"
        onDragEnd={({ active, over }) => props.onChange(moveItemsInArray(files, active, over))}
      >
        <p />
        <SortableContext disabled={files.length === 1} items={files}>
          {files.map((file: ProjectFile, index: number) => (
            <SortableItem
              key={`file-selection-${index}`}
              id={file?.id}
              index={index}
              onRemove={() => removeFile(index)}
              label={file?.fileName}
            >
              <span className={style.label}>
                {file?.fileName}
                <div className={style.sub_label}>{getParentPath(file?.humanReadablePath)}</div>
              </span>
            </SortableItem>
          ))}
        </SortableContext>
      </SortableList>
      <FilePickerControlledDialog
        selectableFileTypes={props.selectableFileTypes}
        baseFilterHint={props.baseFilterHint}
        fileDefinitionTypes={props.fileDefinitionTypes}
        singleSelection={props.singleSelection}
        isSelectableFilter={props.isSelectableFilter}
        onValid={(addFiles: ProjectFile[]) => {
          if (props.singleSelection) {
            props.onChange?.(addFiles);
          } else {
            props.onChange?.(files.concat(addFiles));
          }
          openFilePicker(false);
          return Promise.resolve();
        }}
        isOpen={filePickerOpen}
        onClose={() => openFilePicker(false)}
      />
    </div>
  );
};

// redux form injected props + file picker props
interface IFilePickerFormFieldProps extends IFilePickerFieldProps, Partial<WrappedFieldProps> {
  title?: string | ReactElement;
  tooltip?: string;
  horizontalLayout?: boolean;
}

const FilePickerFormField: FC<IFilePickerFormFieldProps> = (props) => {
  const { isDisabled, selectableFileTypes, singleSelection: exclusiveSelection } = { ...props };
  return (
    <FormInputGroup horizontalLayout={props.horizontalLayout}>
      {props.title && <FormLabel>{props.title}</FormLabel>}
      <FormInputWidget tooltip={props.tooltip}>
        <FilePickerField
          isDisabled={isDisabled}
          selectableFileTypes={selectableFileTypes}
          singleSelection={exclusiveSelection}
          onChange={props.input?.onChange}
          value={props.input?.value}
          noSelectionText={props.noSelectionText}
          baseFilterHint={props.baseFilterHint}
          isSelectableFilter={props.isSelectableFilter}
        />
        {fieldIsInvalid(props) && <FormError>{props.meta.error}</FormError>}
      </FormInputWidget>
    </FormInputGroup>
  );
};

export default FilePickerFormField;

export function FilePickerReduxFormField(props: IFilePickerFormFieldProps & BaseFieldProps<IFilePickerFormFieldProps>) {
  return <Field {...props} component={FilePickerFormField} />;
}
